import type { Clip, Range } from '../queries/edit';

/**
 * The finished video, worked out from the edit: the ranges of the original
 * that survive, with the clips taken from other recordings dropped in where
 * they belong. The API composes the same thing in services/edits.ts, and the
 * two have to agree: the editor draws from this and MediaConvert renders
 * from that.
 *
 * A clip is held by `at`, a second on the original, rather than by its
 * position in the finished video. Cut something out ahead of a clip and the
 * clip stays beside the sentence it was put beside; that is what anybody
 * moving one of these expects, and a position in the output would not
 * survive the next trim.
 */

/** The shortest a clip can be trimmed to. The API enforces the same. */
export const MIN_CLIP_SEC = 0.2;
/** As many clips as the API will take in one recording. */
export const MAX_CLIPS = 20;
const EPSILON = 0.001;

/** One piece of the finished video. Mirrors services/edits.ts on the API. */
export interface Placed {
  kind: 'self' | 'clip';
  /** Seconds inside whatever it comes from: the original, or the clip's source. */
  start: number;
  end: number;
  clip?: Clip;
  /** Where the piece starts in the finished video. */
  outStart: number;
}

/** The finished video, piece by piece. The API composes this identically. */
export function planTimeline(keep: Range[], clips: Clip[], duration: number): Placed[] {
  const ranges = keep.length > 0 ? keep : [[0, duration] as Range];
  const ordered = [...clips].sort((a, b) => a.at - b.at);
  const pieces: Array<Omit<Placed, 'outStart'>> = [];
  let next = 0;
  const pushClip = (clip: Clip) =>
    pieces.push({ kind: 'clip', start: clip.start, end: clip.end, clip });

  for (const [rangeStart, rangeEnd] of ranges) {
    while (next < ordered.length && ordered[next].at <= rangeStart + EPSILON)
      pushClip(ordered[next++]);
    let cursor = rangeStart;
    while (next < ordered.length && ordered[next].at < rangeEnd - EPSILON) {
      const at = Math.max(ordered[next].at, cursor);
      if (at > cursor + EPSILON) pieces.push({ kind: 'self', start: cursor, end: at });
      cursor = at;
      pushClip(ordered[next++]);
    }
    if (rangeEnd > cursor + EPSILON) pieces.push({ kind: 'self', start: cursor, end: rangeEnd });
  }
  while (next < ordered.length) pushClip(ordered[next++]);

  let out = 0;
  return pieces.map((piece) => {
    const placed: Placed = { ...piece, outStart: out };
    out += piece.end - piece.start;
    return placed;
  });
}

export function plannedDuration(plan: Placed[]): number {
  return plan.reduce((total, piece) => total + (piece.end - piece.start), 0);
}

/** A second on the original, moved to where it lands in the finished video. */
export function toOutput(time: number, plan: Placed[]): number | null {
  for (const piece of plan) {
    if (piece.kind !== 'self') continue;
    if (time < piece.start) return null;
    if (time <= piece.end) return piece.outStart + (time - piece.start);
  }
  return null;
}

/**
 * A second in the finished video, read back as a second on the original:
 * where a clip dropped there would go.
 *
 * Between two kept pieces the answer is the start of the one about to play,
 * so a clip dropped on a join lands on the join rather than a frame either
 * side of it.
 */
export function toSource(out: number, plan: Placed[], duration: number): number {
  let last = 0;
  for (const piece of plan) {
    if (piece.kind !== 'self') {
      // A clip already sitting here: dropping on it means the same moment.
      if (out < piece.outStart + (piece.end - piece.start)) return last;
      continue;
    }
    if (out <= piece.outStart + EPSILON) return piece.start;
    if (out <= piece.outStart + (piece.end - piece.start))
      return piece.start + (out - piece.outStart);
    last = piece.end;
  }
  return Math.min(last === 0 ? duration : last, duration);
}
